import {Injectable} from '@angular/core';
import {CollectionViewer, DataSource} from '@angular/cdk/collections';
import {BehaviorSubject, Observable} from 'rxjs';
import {finalize} from 'rxjs/internal/operators';

import {BaseService} from './base.service';

@Injectable({providedIn: 'root'})
export class BaseDatasourceService<T> implements DataSource<T> {

    _moduleSubject = new BehaviorSubject<T[]>([]);
    _loadingSubject = new BehaviorSubject<boolean>(false);

    public loading = this._loadingSubject.asObservable();
    public dataCount = this._moduleSubject.asObservable();
    public numberOfElements = this._moduleSubject.asObservable();

    public totalAmount = 0;
    public syncedCount = 0;
    public notSyncedCount = 0;
    public totalvalue = 0;
    public totalquantity = 0;
    public totalta = 0;
    public totalda = 0;
    public totalDistributorOrderAmount = 0;
    public totalDistributorDeliveryAmount = 0;
    public totalCustomerOrderAmount = 0;
    public totalCustomerDeliveryAmount = 0;
    public totalCustomerReturnAmount = 0;
    public TotalDemandAmount = 0;
    public TotalSaleAmount = 0;
    public TotalDemand = 0;
    public TotalSale = 0;
    public TotalDemandInVolume = 0;
    public TotalDemandInPack = 0;
    public TotalDemandInUnit = 0;
    public TotalSaleInVolume = 0;
    public TotalSaleInPack = 0;
    public TotalSaleInUnit = 0;
    public DemandInVolume = 0;
    public DemandInPack = 0;
    public DemandInUnit = 0;
    public SaleInVolume = 0;
    public SaleInPack = 0;
    public SaleInUnit = 0;

    constructor(private service: BaseService) {
    }

    connect(pCollectionViewer: CollectionViewer): Observable<T[]> {
        return this._moduleSubject.asObservable();
    }

    disconnect(pCollectionViewer: CollectionViewer): void {
        this._moduleSubject.complete();
        this._loadingSubject.complete();
    }

    get data(): any {
        return this._moduleSubject.value.length ? this._moduleSubject.value : [];
    }

    fetchRemoteData(pEndpoint: string, pFilters: object): void {
        this._loadingSubject.next(true);
        this.service.postData(pEndpoint, pFilters)
            .pipe(
                finalize(() => this._loadingSubject.next(false))
            )
            .subscribe(response => {
                this._moduleSubject.next(response['Data']['content']);
                this.dataCount = response['Data']['totalElements'] || response['Data']['content'].length;
                this.totalAmount = response['Data']['TotalAmount'] || 0;
                this.numberOfElements = response['Data']['numberOfElements'] || 0;
                this.syncedCount = response['Data']['SyncedCount'] || 0;
                this.totalvalue = response['Data']['TotalValue'] || 0;
                this.totalquantity = response['Data']['TotalQuantity'] || 0;
                this.notSyncedCount = response['Data']['NotSyncedCount'] || 0;
                this.totalDistributorDeliveryAmount = response['Data']['TotalDistributorDeliveryAmount'] || 0;
                this.totalDistributorOrderAmount = response['Data']['TotalDistributorOrderAmount'] || 0;
                this.totalCustomerOrderAmount = response['Data']['TotalCustomerOrderAmount'] || 0;
                this.totalCustomerDeliveryAmount = response['Data']['TotalCustomerDeliveryAmount'] || 0;
                this.totalCustomerReturnAmount = response['Data']['TotalCustomerReturnAmount'] || 0;
                if (response['Data']['Others']) {
                    this.totalta = response['Data']['Others']['TotalTa'] || 0;
                    this.totalda = response['Data']['Others']['TotalDa'] || 0;
                    this.TotalDemandAmount = response ['Data']['Others']['TotalDemandAmount'] || 0;
                    this.TotalSaleAmount = response ['Data']['Others']['TotalSaleAmount'] || 0;
                    this.TotalDemand = response ['Data']['Others']['TotalDemand'] || 0;
                    this.TotalSale = response ['Data']['Others']['TotalSale'] || 0;
                    this.TotalDemandInVolume = response ['Data']['Others']['TotalDemandInVolume'] || 0;
                    this.TotalDemandInPack = response ['Data']['Others']['TotalDemandInPack'] || 0;
                    this.TotalDemandInUnit = response ['Data']['Others']['TotalDemandInUnit'] || 0;
                    this.TotalSaleInVolume = response ['Data']['Others']['TotalSaleInVolume'] || 0;
                    this.TotalSaleInPack = response ['Data']['Others']['TotalSaleInPack'] || 0;
                    this.TotalSaleInUnit = response ['Data']['Others']['TotalSaleInUnit'] || 0;
                    this.DemandInVolume = response ['Data']['Others']['DemandInVolume'] || 0;
                    this.DemandInPack = response ['Data']['Others']['DemandInPack'] || 0;
                    this.DemandInUnit = response ['Data']['Others']['DemandInUnit'] || 0;
                    this.SaleInVolume = response ['Data']['Others']['SaleInVolume'] || 0;
                    this.SaleInPack = response ['Data']['Others']['SaleInPack'] || 0;
                    this.SaleInUnit = response ['Data']['Others']['SaleInUnit'] || 0;
                }
            });
    }

    fetchPostDataForMicroservice(pEndpoint: string, pFilters: object): void {
        this._loadingSubject.next(true);
        this.service.postDataForMicroService(pEndpoint, pFilters)
            .pipe(
                finalize(() => this._loadingSubject.next(false))
            )
            .subscribe(response => {
                this._moduleSubject.next(response['data']['content']);
                this.dataCount = response['data']['page'].totalElements || response['data']['content'].length;
                this.numberOfElements = response['data']['content'].length;
            });
    }

    fetchGetDataForMicroservice(pEndpoint: string, pFilters: object): void {
        this._loadingSubject.next(true);
        this.service.getDataForMicroService(pEndpoint, pFilters)
            .pipe(
                finalize(() => this._loadingSubject.next(false))
            )
            .subscribe(response => {
                this._moduleSubject.next(response['data']['content']);
                this.numberOfElements = response['data']['page'].totalElements || response['data']['content'].length;
                this.dataCount = response['data']['page'].totalElements || response['data']['content'].length;
            });
    }

    fetchGetData(pEndpoint: string, pFilters: object): void {
        this._loadingSubject.next(true);
        this.service.getData(pEndpoint, pFilters)
            .pipe(
                finalize(() => this._loadingSubject.next(false))
            )
            .subscribe(response => {
                this._moduleSubject.next(response['Data']['content']);
                this.dataCount = response['Data']['totalElements'] || response['Data']['content'].length;
            });
    }

    setLocalData(pDataArray: any, totalElements: any, numberOfElements?: any): void {
        this._moduleSubject.next(pDataArray);
        this.dataCount = totalElements;
        this.numberOfElements = numberOfElements;
    }
}
